export const clampDiscountRate = (value) => {
  const rate = Number(value || 0);
  if (Number.isNaN(rate)) return 0;
  return Math.min(100, Math.max(0, rate));
};

export const resolveItemUnitPrice = (item) => {
  const variantName = String(item?.variantName || "").trim();
  const variants = Array.isArray(item?.product?.variants)
    ? item.product.variants
    : [];

  if (variantName) {
    const matchedVariant = variants.find(
      (variant) =>
        String(variant?.name || "")
          .trim()
          .toLowerCase() === variantName.toLowerCase(),
    );

    if (matchedVariant && Number.isFinite(Number(matchedVariant.price))) {
      return Number(matchedVariant.price);
    }
  }

  return Number(item?.product?.price || 0);
};

export const resolveItemDisplayVariantName = (item) => {
  const variantName = String(item?.variantName || "").trim();
  if (variantName) {
    return variantName;
  }

  const variants = Array.isArray(item?.product?.variants)
    ? item.product.variants
    : [];
  if (variants.length > 1) {
    return "";
  }

  return String(item?.product?.type || "").trim();
};

export const resolveProductImageUrl = (product, options = {}) => {
  const { sortByOrder = false } = options;
  const images = Array.isArray(product?.images) ? product.images : [];

  if (images.length > 0) {
    const source = sortByOrder
      ? [...images].sort((a, b) => {
          const aOrder =
            typeof a === "object" && a !== null ? Number(a.order ?? 0) : 0;
          const bOrder =
            typeof b === "object" && b !== null ? Number(b.order ?? 0) : 0;
          return aOrder - bOrder;
        })
      : images;

    const firstImage = source[0];

    if (typeof firstImage === "string" && firstImage.trim()) {
      return firstImage;
    }

    if (
      firstImage &&
      typeof firstImage === "object" &&
      typeof firstImage.url === "string"
    ) {
      return firstImage.url.trim();
    }
  }

  if (typeof product?.image === "string" && product.image.trim()) {
    return product.image.trim();
  }

  return "";
};

const normalizeCategoryValue = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const getProductCategoryList = (product) => {
  if (Array.isArray(product?.categories)) {
    return product.categories.map(normalizeCategoryValue).filter(Boolean);
  }

  if (typeof product?.category === "string") {
    return [normalizeCategoryValue(product.category)].filter(Boolean);
  }

  if (typeof product?.categories === "string") {
    return product.categories
      .split(",")
      .map(normalizeCategoryValue)
      .filter(Boolean);
  }

  return [];
};

const getMainCategorySegment = (product) => {
  const mainCategory = getProductCategoryList(product)[0] || "others";

  return mainCategory
    .replace(/\s*&\s*/g, "&")
    .replace(/\s+/g, "")
    .replace(/[^a-z0-9&-]/g, "");
};

const stopwords = [
  "a",
  "an",
  "the",
  "and",
  "or",
  "but",
  "if",
  "then",
  "else",
  "for",
  "of",
  "to",
  "in",
  "on",
  "at",
  "by",
  "from",
  "with",
  "as",
  "is",
  "are",
  "was",
  "were",
  "be",
  "been",
  "this",
  "that",
  "these",
  "those",
  "it",
  "its",
  "your",
  "my",
  "our",
  "their",
  "you",
  "we",
  "they",
  "very",
  "really",
  "so",
  "too",
];

const lowValueWords = [
  "product",
  "item",
  "thing",
  "stuff",
  "set",
  "pack",
  "bundle",
  "new",
  "best",
  "good",
  "nice",
  "premium",
  "quality",
  "original",
  "authentic",
  "sale",
  "hot",
  "popular",
  "comfortable",
  "soft",
  "fluffy",
  "fashion",
  "style",
  "design",
  "toy",
  "accessory",
  "tool",
  "gift",
  "perfect",
  "latest",
];

const slugSkipWords = new Set([...stopwords, ...lowValueWords]);

const getNameSlugSegment = (name) => {
  const words = String(name || "")
    .toLowerCase()
    .replace(/\./g, "")
    .replace(/[^a-z0-9\s-]/g, " ")
    .split(/\s+/)
    .filter((word) => word && !slugSkipWords.has(word))
    .slice(0, 6);

  return words.join("-") || "item";
};

export const buildProductDetailsPath = (product) => {
  const productId = String(product?._id || "").trim();
  if (!productId) {
    return "/products";
  }

  const categorySegment = getMainCategorySegment(product);
  const slugSegment = getNameSlugSegment(product?.name);

  return `/products/${categorySegment}/${slugSegment}/${productId}`;
};
